/**
 * Index Calculator — pure aggregation of a Case[] into an IndexSnapshot.
 *
 * Duplicated from `packages/server/src/domain/indexCalculator.ts` because
 * `@aim/server` is a Node/Fastify entry point (its `main` boots a server on
 * import) and cannot be bundled into the browser. Keeping the function
 * self-contained here — dependent only on the shared `@aim/types` shapes —
 * lets the SPA compute the index and timeline entirely on the client, which
 * is what the published (read-only) mode requires anyway.
 *
 * Behavioural contract (Requirement 10):
 *  - `maturity` counts cases with `status === "Production"` (strict, case-sensitive).
 *  - `total` equals `cases.length`; on empty input every counter is 0.
 *  - `prod + dev + pipe === total` for every input.
 *  - `revenueTotal`, `adoptionTotal`, `tasksTotal` treat null/undefined as 0.
 *  - `ttvAvg` / `productivityAvg` are the arithmetic mean of positive samples,
 *    rounded half-away-from-zero; `null` when no positive samples exist.
 *  - `deltaVsPrevious` is 0 when `cases.length ≤ 1`; otherwise it is
 *    `maturity` minus the count of Production cases in a copy sorted by
 *    `dateISO` ascending with the last element removed. Cases whose
 *    `dateISO` is missing / non-parseable sort **last** (10.11).
 *  - The function is pure: it does not mutate the input array or any nested
 *    Case.
 */

import type { Case, IndexSnapshot } from "@aim/types";

/** Half-away-from-zero rounding (symmetric around zero). */
function roundHalfAwayFromZero(x: number): number {
  if (x === 0) return 0;
  return Math.sign(x) * Math.floor(Math.abs(x) + 0.5);
}

/** Coerce nullish or non-finite numerics to 0 so running totals stay finite. */
function numOrZero(v: number | null | undefined): number {
  return typeof v === "number" && Number.isFinite(v) ? v : 0;
}

function isPositiveFinite(v: number | null | undefined): v is number {
  return typeof v === "number" && Number.isFinite(v) && v > 0;
}

function parseDateOrNull(dateISO: string | null | undefined): number | null {
  if (typeof dateISO !== "string" || dateISO.length === 0) return null;
  const t = Date.parse(dateISO);
  return Number.isFinite(t) ? t : null;
}

/**
 * Compute the `IndexSnapshot` for a `Case[]` portfolio. Pure and total —
 * does not mutate its argument and always returns a well-formed snapshot,
 * even for malformed inputs.
 */
export function computeIndex(cases: readonly Case[]): IndexSnapshot {
  let prod = 0;
  let dev = 0;
  let pipe = 0;
  let revenueTotal = 0;
  let adoptionTotal = 0;
  let tasksTotal = 0;
  let ttvSum = 0;
  let ttvCount = 0;
  let prodSum = 0;
  let prodCount = 0;

  for (const c of cases) {
    if (c.status === "Production") {
      prod += 1;
    } else if (c.status === "Development") {
      dev += 1;
    } else {
      pipe += 1;
    }

    revenueTotal += numOrZero(c.revenue);
    adoptionTotal += numOrZero(c.adoption);
    tasksTotal += numOrZero(c.tasks);

    if (isPositiveFinite(c.ttv)) {
      ttvSum += c.ttv;
      ttvCount += 1;
    }
    if (isPositiveFinite(c.productivity)) {
      prodSum += c.productivity;
      prodCount += 1;
    }
  }

  let deltaVsPrevious = 0;
  if (cases.length > 1) {
    // Shallow copy so we never mutate the input array.
    const sorted = [...cases].sort((a, b) => {
      const ta = parseDateOrNull(a.dateISO);
      const tb = parseDateOrNull(b.dateISO);
      if (ta === null && tb === null) return 0;
      if (ta === null) return 1;
      if (tb === null) return -1;
      return ta - tb;
    });

    let previousMaturity = 0;
    for (let i = 0; i < sorted.length - 1; i += 1) {
      if (sorted[i]?.status === "Production") previousMaturity += 1;
    }
    deltaVsPrevious = prod - previousMaturity;
  }

  return {
    maturity: prod,
    total: cases.length,
    prod,
    dev,
    pipe,
    revenueTotal,
    adoptionTotal,
    tasksTotal,
    ttvAvg: ttvCount > 0 ? roundHalfAwayFromZero(ttvSum / ttvCount) : null,
    productivityAvg:
      prodCount > 0 ? roundHalfAwayFromZero(prodSum / prodCount) : null,
    deltaVsPrevious,
  };
}
